/**
 * Rows of the printable observation sheet of one pupil.
 *
 * Kept out of the page so the ordering and the lesson lookup can be tested
 * without a DOM. Two rules the screen does not follow: the paper record reads
 * oldest first, and `lessonId` is resolved by hand rather than joined — a
 * deleted lesson must still leave its observations readable.
 */

import { parseLocalDate } from './dates.ts';
import { linkedLessonIds } from './lesson-links.ts';

/** The part of an observation the sheet needs. */
export interface SheetObservation {
  id: string;
  date: string;
  lessonId: string;
  extraLessonIds: readonly string[];
  subject: string;
  difficulty: string;
  note: string;
  support: string;
  hasHandwriting: boolean;
}

/** The part of a lesson the sheet needs. */
export interface SheetLesson {
  id: string;
  lessonNumber: string;
  title: string;
}

export interface ObservationRow {
  id: string;
  /** "24/09/2026", or '' when the observation carries no usable date. */
  date: string;
  subject: string;
  /** The lesson it was taken during — "FR-12 · Les fractions" — or ''. */
  lesson: string;
  difficulty: string;
  note: string;
  support: string;
  hasHandwriting: boolean;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** "24/09/2026" — short enough for a table column, unambiguous on paper. */
export function frSheetDate(iso: string): string {
  if (!ISO_DATE.test(iso)) return '';
  return parseLocalDate(iso).toLocaleDateString('fr-BE');
}

export function observationRows(
  observations: readonly SheetObservation[],
  lessons: readonly SheetLesson[],
): ObservationRow[] {
  const byId = new Map(lessons.map((l) => [l.id, l]));

  return observations
    .map((o, index) => ({ o, index }))
    // Undated notes sort last rather than first: an empty string would open
    // the sheet with the rows that say the least about when they happened.
    .sort((a, b) => (a.o.date || '9999').localeCompare(b.o.date || '9999') || a.index - b.index)
    .map(({ o }) => {
      // The lesson it was taken during, or a copy of it once that one has been
      // deleted: the copy carries the same number and title.
      const lesson = linkedLessonIds(o)
        .map((id) => byId.get(id))
        .find((l) => l !== undefined);
      return {
        id: o.id,
        date: frSheetDate(o.date),
        subject: o.subject,
        lesson: lesson ? [lesson.lessonNumber, lesson.title].filter(Boolean).join(' · ') : '',
        difficulty: o.difficulty,
        note: o.note,
        support: o.support,
        hasHandwriting: o.hasHandwriting,
      };
    });
}

/** "du 24/09/2026 au 12/12/2026" for the sheet header, or '' if undatable. */
export function observationPeriod(rows: readonly ObservationRow[]): string {
  const dated = rows.filter((r) => r.date);
  const first = dated[0];
  const last = dated[dated.length - 1];
  if (!first || !last) return '';
  return first === last ? `le ${first.date}` : `du ${first.date} au ${last.date}`;
}
